import { useState, type MouseEvent } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { CustomButton } from "./CustomButton";

type CustomAlertDialogProps = {
  btnTitle?: string;
  btnType?: "ai" | "standard";
  alertTitle: string;
  alertDescription?: string;
  alertType: "confirm" | "delete" | "replace";
  children?: React.ReactNode;
  withButton?: boolean;
  confirm: (e: MouseEvent<HTMLButtonElement>) => void;
  close?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  /** Controls the dialog from outside; otherwise the trigger button opens it. */
  isOpen?: boolean;
  onClose?: () => void;
};

export const CustomAlertDialog = ({
  disabled = false,
  btnTitle,
  btnType = "standard",
  alertTitle,
  alertDescription,
  alertType = "confirm",
  children,
  withButton = true,
  confirm = () => {},
  close = () => {},
  isLoading,
  isOpen: controlledOpen,
  onClose: controlledOnClose
}: CustomAlertDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const onOpen = () => setInternalOpen(true);
  // After confirm, a controlled dialog stays open until its owner closes it
  // (e.g. once an async delete finishes).
  const onClose = () => {
    setInternalOpen(false);
    controlledOnClose?.();
  };
  const onConfirmed = () => setInternalOpen(false);

  const renderBtnText = () => {
    if (alertType === "delete") return "Delete";
    if (alertType === "replace") return "Replace";
    return "Confirm";
  };
  const btnConfirmText = renderBtnText();
  return (
    <>
      {withButton && (
        <CustomButton
          isDisabled={disabled}
          title={btnTitle ?? ""}
          onClick={onOpen}
          type={btnType}
        />
      )}
      <AlertDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (open) return;
          close();
          onClose();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertTitle}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-2">
                {alertDescription}
                {children}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                close();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant={alertType === "confirm" ? "primary" : "danger"}
              disabled={isLoading}
              onClick={(e) => {
                confirm(e);
                onConfirmed();
              }}
            >
              {isLoading ? <Spinner /> : null}
              {btnConfirmText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
